import Seo from "../components/common/Seo";
import PageHeader from "../components/common/PageHeader";
import { Reveal } from "../components/common/Reveal";
import Cta from "../components/common/Cta";
import FinalCTA from "../components/sections/FinalCTA";
import { services, servicePricing } from "../lib/content";
import { Fragment, useState } from "react";
import PricingModal from "../components/sections/PricingModal";

function Block({ label, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel mb-3">{label}</div>
      {children}
    </div>
  );
}

const PEOPLE = {
  "business-development": { src: "/assets/crm/benny-cutout.png", name: "Benny Lovell", title: "Founder, CFO" },
  "desktop-ai-agent-deployment": { src: "/assets/crm/benny-cutout.png", name: "Benny Lovell", title: "Founder, CFO" },
  "growth-strategy": { src: "/assets/crm/axel-cutout.png", name: "Axel Richard", title: "CEO" },
  "process-automation": { src: "/assets/crm/axel-cutout.png", name: "Axel Richard", title: "CEO" },
  "sales-infrastructure": { src: "/assets/crm/yasi-cutout.png", name: "Yasiel Sambra", title: "COO" },
  "web-design": { src: "/assets/crm/yasi-cutout.png", name: "Yasiel Sambra", title: "COO" },
};

function PersonVisual({ src, name, title, slug }) {
  return (
    <div className="mt-10 max-w-[300px]" data-testid={`person-visual-${slug}`}>
      <div className="relative">
        <img
          src={src}
          alt={`${name} — ${title} at Kratos Venture Group`}
          className="block w-full h-auto select-none"
          loading="lazy"
        />
      </div>
      <div className="mt-4">
        <div className="font-display text-lg font-medium tracking-tight text-kv-off">{name}</div>
        <div className="font-body text-sm text-kv-grey">{title}</div>
      </div>
    </div>
  );
}

function CrmVisual() {
  return (
    <div className="mt-10 max-w-[300px]" data-testid="crm-visual">
      <div className="relative">
        <img
          src="/assets/crm/chris-cutout.png"
          alt="Chris Meneses — CRM Director at Kratos Venture Group"
          className="block w-full h-auto select-none"
          loading="lazy"
        />
      </div>
      <div className="mt-4">
        <div className="font-display text-lg font-medium tracking-tight text-kv-off" data-testid="crm-person-name">Chris Meneses</div>
        <div className="font-body text-sm text-kv-grey" data-testid="crm-person-title">CRM Director</div>
      </div>
      <div className="mt-5 flex items-center gap-2.5">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-kv-steel whitespace-nowrap">Powered by</span>
        <img src="/assets/crm/close-logo-trim.png" alt="Close.io CRM platform" className="h-7 w-auto" />
      </div>
      <div className="mt-3 inline-flex items-center gap-2" data-testid="crm-partner-microline">
        <span className="h-1.5 w-1.5 rounded-full bg-kv-electric" aria-hidden />
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-kv-steel">Certified Close.io partner</span>
      </div>
    </div>
  );
}

export default function Services() {
  const [pricing, setPricing] = useState(null);
  return (
    <>
      <Seo
        title="Services — Kratos Venture Group"
        description="Business development, growth strategy, Mythoros© deployment, automation, sales infrastructure, CRM, Voice AI, consulting, integration, and ongoing optimization."
        path="/services"
      />
      <PageHeader
        eyebrow="Services"
        title="Strategy, systems, and intelligent execution."
        intro="We design and implement a connected operating model around your business — from strategy through deployment and ongoing optimization."
      />

      <section className="relative pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="border-t border-kv-steel/15">
            {services.map((s, i) => {
              const isCrm = s.slug === "crm-development";
              const person = PEOPLE[s.slug];
              const hasVisual = isCrm || Boolean(person);
              return (
              <Fragment key={s.slug}>
              <Reveal>
                <article
                  id={s.slug}
                  data-testid={`service-${s.slug}`}
                  className={`relative grid grid-cols-1 lg:grid-cols-12 gap-8 py-14 border-b border-kv-steel/15 scroll-mt-28 ${hasVisual ? "overflow-hidden" : ""}`}
                >
                  {isCrm && (
                    <img
                      src="/assets/crm/close-icon.png"
                      alt=""
                      aria-hidden
                      className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] md:w-[560px] max-w-none opacity-[0.09]"
                    />
                  )}
                  {person && (
                    <img
                      src="/assets/crm/kratos-icon.png"
                      alt=""
                      aria-hidden
                      className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] md:w-[560px] max-w-none opacity-[0.09]"
                    />
                  )}
                  <div className="relative lg:col-span-4">
                    <span className="block h-1 w-8 bg-kv-electric/80" aria-hidden />
                    <h2 className="mt-4 font-display text-2xl md:text-3xl font-medium tracking-tight text-kv-off">{s.title}</h2>
                    <p className="mt-4 font-body text-sm md:text-base text-kv-grey leading-relaxed">{s.problem}</p>
                    <div className="mt-6">
                      <Cta to="/contact" variant="secondary" className="px-5 py-2.5 text-[13px]" testId={`service-cta-${s.slug}`}>
                        Book a Strategy Call
                      </Cta>
                    </div>
                    {isCrm && <CrmVisual />}
                    {person && (
                      <PersonVisual src={person.src} name={person.name} title={person.title} slug={s.slug} />
                    )}
                  </div>

                  <div className="relative lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <Block label="Kratos approach">
                      <p className="font-body text-sm md:text-base text-kv-off leading-relaxed">{s.approach}</p>
                      <p className="mt-4 font-body text-sm text-kv-steel">Expected impact — {s.impact}</p>
                    </Block>
                    <Block label="Deliverables">
                      <ul className="space-y-2">
                        {s.deliverables.map((d) => (
                          <li key={d} className="flex items-center gap-2 font-body text-sm text-kv-grey">
                            <span className="h-1 w-1 rounded-full bg-kv-electric" /> {d}
                          </li>
                        ))}
                      </ul>
                    </Block>
                    <Block label="Implementation steps">
                      <div className="flex flex-wrap gap-2">
                        {s.steps.map((st, idx) => (
                          <span key={st} className="font-mono text-[11px] uppercase tracking-wide text-kv-grey border border-kv-steel/25 rounded-md px-3 py-1.5">
                            {st}
                          </span>
                        ))}
                      </div>
                    </Block>
                    <Block label="Related">
                      <div className="flex flex-col gap-2">
                        {servicePricing[s.slug] && (
                          <Cta
                            variant="ghost"
                            className="justify-start px-0 py-1 text-sm !text-kv-off hover:!text-kv-electric"
                            testId={`service-pricing-${s.slug}`}
                            onClick={() => setPricing(servicePricing[s.slug])}
                            arrow
                          >
                            View pricing
                          </Cta>
                        )}
                        <Cta to="/mythoros" variant="ghost" className="justify-start px-0 py-1 text-sm" testId={`service-related-agent-${s.slug}`}>Mythoros© →</Cta>
                        <Cta to="/process" variant="ghost" className="justify-start px-0 py-1 text-sm" testId={`service-related-process-${s.slug}`}>Our Process →</Cta>
                      </div>
                    </Block>
                  </div>
                </article>
              </Reveal>
              {i === 6 && (
                <div className="w-screen ml-[calc(50%-50vw)] my-6 bg-kv-bg2 border-y border-kv-steel/15" data-testid="services-editorial-band">
                  <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                    <p className="font-display text-2xl md:text-3xl font-medium tracking-tight text-kv-off max-w-2xl leading-snug text-balance">
                      Most of these work best as one connected operating model — not a set of isolated projects.
                    </p>
                    <Cta to="/contact" testId="services-band-cta" arrow>Book a Strategy Call</Cta>
                  </div>
                </div>
              )}
              </Fragment>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
      <PricingModal group={pricing} onClose={() => setPricing(null)} />
    </>
  );
}
